import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface ScheduledReport {
  id: string
  name: string
  description: string
  reportType: string
  frequency: string
  dayOfWeek?: number
  dayOfMonth?: number
  timeOfDay: string
  timezone: string
  outputFormat: string
  deliveryMethod: string
  deliveryConfig: Record<string, unknown>
  filters: Record<string, unknown>
  status: string
  lastRun?: string
  nextRun?: string
  runCount: number
  createdBy: string
  createdAt: string
}

interface ReportExecution {
  id: string
  scheduleId: string
  scheduleName: string
  reportType: string
  status: string
  startedAt: string
  completedAt?: string
  duration: number
  outputFormat: string
  outputSize: number
  outputUrl?: string
  deliveryStatus: string
  deliveredTo: string[]
  error?: string
}

interface Statistics {
  totalSchedules: number
  activeSchedules: number
  pausedSchedules: number
  totalExecutions: number
  completedExecutions: number
  failedExecutions: number
  successRate: number
  byType: Record<string, number>
  byFrequency: Record<string, number>
}

const API_BASE = '/api/scheduled-reports'

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

const formatDateTime = (isoString: string | undefined): string => {
  if (!isoString) return '-'
  return new Date(isoString).toLocaleString()
}

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

export default function ScheduledReports() {
  const [activeTab, setActiveTab] = useState<'schedules' | 'executions' | 'create' | 'statistics'>('schedules')
  const [schedules, setSchedules] = useState<ScheduledReport[]>([])
  const [executions, setExecutions] = useState<ReportExecution[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedSchedule, setSelectedSchedule] = useState<ScheduledReport | null>(null)

  // Form state
  const [reportTypes, setReportTypes] = useState<{ id: string; name: string; description: string }[]>([])
  const [frequencies, setFrequencies] = useState<{ id: string; name: string; description: string }[]>([])
  const [outputFormats, setOutputFormats] = useState<{ id: string; name: string }[]>([])
  const [deliveryMethods, setDeliveryMethods] = useState<{ id: string; name: string; description: string }[]>([])

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    reportType: '',
    frequency: '',
    dayOfWeek: 1,
    dayOfMonth: 1,
    timeOfDay: '09:00',
    timezone: 'Europe/London',
    outputFormat: 'pdf',
    deliveryMethod: 'email',
    recipients: '',
    webhookUrl: '',
    filters: '{}',
  })

  useEffect(() => {
    loadData()
    loadFormOptions()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      const [schedulesRes, executionsRes, statsRes] = await Promise.all([
        fetch(`${API_BASE}/schedules`),
        fetch(`${API_BASE}/executions`),
        fetch(`${API_BASE}/statistics`),
      ])

      if (schedulesRes.ok) {
        const data = await schedulesRes.json()
        setSchedules(data.schedules || [])
      }
      if (executionsRes.ok) {
        const data = await executionsRes.json()
        setExecutions(data.executions || [])
      }
      if (statsRes.ok) {
        setStatistics(await statsRes.json())
      }
    } catch (err) {
      setError('Failed to load data')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const loadFormOptions = async () => {
    try {
      const [typesRes, freqRes, formatsRes, deliveryRes] = await Promise.all([
        fetch(`${API_BASE}/report-types`),
        fetch(`${API_BASE}/frequencies`),
        fetch(`${API_BASE}/output-formats`),
        fetch(`${API_BASE}/delivery-methods`),
      ])

      if (typesRes.ok) setReportTypes(await typesRes.json())
      if (freqRes.ok) setFrequencies(await freqRes.json())
      if (formatsRes.ok) setOutputFormats(await formatsRes.json())
      if (deliveryRes.ok) setDeliveryMethods(await deliveryRes.json())
    } catch (err) {
      console.error('Failed to load form options:', err)
    }
  }

  const handleCreateSchedule = async () => {
    try {
      const deliveryConfig: Record<string, unknown> = {}
      if (formData.deliveryMethod === 'email') {
        deliveryConfig.recipients = formData.recipients.split(',').map((r) => r.trim())
        deliveryConfig.includeAttachment = true
      } else if (formData.deliveryMethod === 'webhook') {
        deliveryConfig.webhookUrl = formData.webhookUrl
      }

      const response = await fetch(`${API_BASE}/schedules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          report_type: formData.reportType,
          frequency: formData.frequency,
          time_of_day: formData.timeOfDay,
          timezone: formData.timezone,
          output_format: formData.outputFormat,
          delivery_method: formData.deliveryMethod,
          delivery_config: deliveryConfig,
          filters: JSON.parse(formData.filters || '{}'),
          day_of_week: ['weekly', 'biweekly'].includes(formData.frequency) ? formData.dayOfWeek : null,
          day_of_month: ['monthly', 'quarterly'].includes(formData.frequency) ? formData.dayOfMonth : null,
        }),
      })

      if (response.ok) {
        await loadData()
        setActiveTab('schedules')
        setFormData({
          name: '',
          description: '',
          reportType: '',
          frequency: '',
          dayOfWeek: 1,
          dayOfMonth: 1,
          timeOfDay: '09:00',
          timezone: 'Europe/London',
          outputFormat: 'pdf',
          deliveryMethod: 'email',
          recipients: '',
          webhookUrl: '',
          filters: '{}',
        })
      }
    } catch (err) {
      console.error('Failed to create schedule:', err)
    }
  }

  const handlePauseResume = async (scheduleId: string, isPaused: boolean) => {
    try {
      const endpoint = isPaused ? 'resume' : 'pause'
      const response = await fetch(`${API_BASE}/schedules/${scheduleId}/${endpoint}`, {
        method: 'POST',
      })
      if (response.ok) {
        await loadData()
      }
    } catch (err) {
      console.error('Failed to pause/resume schedule:', err)
    }
  }

  const handleRunNow = async (scheduleId: string) => {
    try {
      const response = await fetch(`${API_BASE}/schedules/${scheduleId}/run`, {
        method: 'POST',
      })
      if (response.ok) {
        await loadData()
        setActiveTab('executions')
      }
    } catch (err) {
      console.error('Failed to run schedule:', err)
    }
  }

  const handleDelete = async (scheduleId: string) => {
    if (!confirm('Are you sure you want to delete this scheduled report?')) return

    try {
      const response = await fetch(`${API_BASE}/schedules/${scheduleId}`, {
        method: 'DELETE',
      })
      if (response.ok) {
        await loadData()
        setSelectedSchedule(null)
      }
    } catch (err) {
      console.error('Failed to delete schedule:', err)
    }
  }

  const getStatusBadge = (status: string) => {
    const colors: Record<string, { bg: string; text: string }> = {
      active: { bg: '#D1FAE5', text: '#065F46' },
      paused: { bg: '#FEF3C7', text: '#92400E' },
      completed: { bg: '#D1FAE5', text: '#065F46' },
      running: { bg: '#DBEAFE', text: '#1E40AF' },
      failed: { bg: '#FEE2E2', text: '#991B1B' },
      pending: { bg: '#F3F4F6', text: '#374151' },
      delivered: { bg: '#D1FAE5', text: '#065F46' },
    }
    const style = colors[status] || colors.pending
    return (
      <span
        style={{
          padding: '2px 8px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: 500,
          backgroundColor: style.bg,
          color: style.text,
        }}
      >
        {status}
      </span>
    )
  }

  const renderSchedules = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0, color: '#1F2937' }}>Scheduled Reports</h3>
        <button
          onClick={() => setActiveTab('create')}
          style={{
            padding: '8px 16px',
            backgroundColor: '#4A90E2',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: 500,
          }}
        >
          + Create Schedule
        </button>
      </div>

      {loading ? (
        <p>Loading schedules...</p>
      ) : schedules.length === 0 ? (
        <p style={{ color: '#6B7280' }}>No scheduled reports found. Create one to get started.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {schedules.map((schedule) => (
            <div
              key={schedule.id}
              style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                border: '1px solid #E5E7EB',
                padding: '16px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <h4 style={{ margin: 0, color: '#1F2937' }}>{schedule.name}</h4>
                    {getStatusBadge(schedule.status)}
                  </div>
                  <p style={{ margin: '0 0 8px 0', color: '#6B7280', fontSize: '14px' }}>{schedule.description}</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', fontSize: '13px', color: '#4B5563' }}>
                    <span>
                      <strong>Type:</strong> {schedule.reportType}
                    </span>
                    <span>
                      <strong>Frequency:</strong> {schedule.frequency}
                    </span>
                    <span>
                      <strong>Time:</strong> {schedule.timeOfDay} {schedule.timezone}
                    </span>
                    <span>
                      <strong>Format:</strong> {schedule.outputFormat.toUpperCase()}
                    </span>
                    <span>
                      <strong>Delivery:</strong> {schedule.deliveryMethod}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#9CA3AF', marginTop: '8px' }}>
                    <span>Last run: {formatDateTime(schedule.lastRun)}</span>
                    <span>Next run: {formatDateTime(schedule.nextRun)}</span>
                    <span>Total runs: {schedule.runCount}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => handleRunNow(schedule.id)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#10B981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '13px',
                    }}
                  >
                    Run Now
                  </button>
                  <button
                    onClick={() => handlePauseResume(schedule.id, schedule.status === 'paused')}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: schedule.status === 'paused' ? '#4A90E2' : '#F59E0B',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '13px',
                    }}
                  >
                    {schedule.status === 'paused' ? 'Resume' : 'Pause'}
                  </button>
                  <button
                    onClick={() => setSelectedSchedule(schedule)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#6B7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '13px',
                    }}
                  >
                    Details
                  </button>
                  <button
                    onClick={() => handleDelete(schedule.id)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#EF4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '13px',
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )

  const renderExecutions = () => (
    <div>
      <h3 style={{ margin: '0 0 16px 0', color: '#1F2937' }}>Execution History</h3>

      {loading ? (
        <p>Loading executions...</p>
      ) : executions.length === 0 ? (
        <p style={{ color: '#6B7280' }}>No executions found.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#F9FAFB' }}>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #E5E7EB', fontWeight: 600 }}>
                Report
              </th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #E5E7EB', fontWeight: 600 }}>
                Type
              </th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #E5E7EB', fontWeight: 600 }}>
                Status
              </th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #E5E7EB', fontWeight: 600 }}>
                Started
              </th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #E5E7EB', fontWeight: 600 }}>
                Duration
              </th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #E5E7EB', fontWeight: 600 }}>
                Size
              </th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #E5E7EB', fontWeight: 600 }}>
                Delivery
              </th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #E5E7EB', fontWeight: 600 }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {executions.map((exec) => (
              <tr key={exec.id} style={{ borderBottom: '1px solid #E5E7EB' }}>
                <td style={{ padding: '12px' }}>{exec.scheduleName}</td>
                <td style={{ padding: '12px' }}>{exec.reportType}</td>
                <td style={{ padding: '12px' }}>{getStatusBadge(exec.status)}</td>
                <td style={{ padding: '12px', fontSize: '13px' }}>{formatDateTime(exec.startedAt)}</td>
                <td style={{ padding: '12px' }}>{exec.duration}s</td>
                <td style={{ padding: '12px' }}>{formatBytes(exec.outputSize)}</td>
                <td style={{ padding: '12px' }}>{getStatusBadge(exec.deliveryStatus)}</td>
                <td style={{ padding: '12px' }}>
                  {exec.outputUrl && exec.status === 'completed' ? (
                    <a
                      href={exec.outputUrl}
                      style={{ color: '#4A90E2', textDecoration: 'none', fontSize: '13px' }}
                    >
                      Download
                    </a>
                  ) : exec.error ? (
                    <span style={{ color: '#EF4444', fontSize: '12px' }} title={exec.error}>
                      View Error
                    </span>
                  ) : (
                    '-'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )

  const renderCreateForm = () => (
    <div style={{ maxWidth: '800px' }}>
      <h3 style={{ margin: '0 0 24px 0', color: '#1F2937' }}>Create Scheduled Report</h3>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500, color: '#374151' }}>Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '14px',
            }}
            placeholder="Weekly Market Report"
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500, color: '#374151' }}>Report Type *</label>
          <select
            value={formData.reportType}
            onChange={(e) => setFormData({ ...formData, reportType: e.target.value })}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '14px',
            }}
          >
            <option value="">Select type...</option>
            {reportTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </div>

        <div style={{ gridColumn: '1 / -1' }}>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500, color: '#374151' }}>Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '14px',
              minHeight: '60px',
            }}
            placeholder="Optional description of this scheduled report"
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500, color: '#374151' }}>Frequency *</label>
          <select
            value={formData.frequency}
            onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '14px',
            }}
          >
            <option value="">Select frequency...</option>
            {frequencies.map((freq) => (
              <option key={freq.id} value={freq.id}>
                {freq.name}
              </option>
            ))}
          </select>
        </div>

        {['weekly', 'biweekly'].includes(formData.frequency) && (
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500, color: '#374151' }}>Day of Week</label>
            <select
              value={formData.dayOfWeek}
              onChange={(e) => setFormData({ ...formData, dayOfWeek: parseInt(e.target.value) })}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '14px',
              }}
            >
              {DAYS_OF_WEEK.map((day, index) => (
                <option key={index} value={index}>
                  {day}
                </option>
              ))}
            </select>
          </div>
        )}

        {['monthly', 'quarterly'].includes(formData.frequency) && (
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500, color: '#374151' }}>Day of Month</label>
            <select
              value={formData.dayOfMonth}
              onChange={(e) => setFormData({ ...formData, dayOfMonth: parseInt(e.target.value) })}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '14px',
              }}
            >
              {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                <option key={day} value={day}>
                  {day}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500, color: '#374151' }}>Time</label>
          <input
            type="time"
            value={formData.timeOfDay}
            onChange={(e) => setFormData({ ...formData, timeOfDay: e.target.value })}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '14px',
            }}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500, color: '#374151' }}>Timezone</label>
          <select
            value={formData.timezone}
            onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '14px',
            }}
          >
            <option value="UTC">UTC</option>
            <option value="Europe/London">Europe/London</option>
            <option value="Europe/Paris">Europe/Paris</option>
            <option value="Europe/Berlin">Europe/Berlin</option>
            <option value="America/New_York">America/New_York</option>
            <option value="America/Los_Angeles">America/Los_Angeles</option>
          </select>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500, color: '#374151' }}>Output Format</label>
          <select
            value={formData.outputFormat}
            onChange={(e) => setFormData({ ...formData, outputFormat: e.target.value })}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '14px',
            }}
          >
            {outputFormats.map((format) => (
              <option key={format.id} value={format.id}>
                {format.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500, color: '#374151' }}>Delivery Method</label>
          <select
            value={formData.deliveryMethod}
            onChange={(e) => setFormData({ ...formData, deliveryMethod: e.target.value })}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '14px',
            }}
          >
            {deliveryMethods.map((method) => (
              <option key={method.id} value={method.id}>
                {method.name}
              </option>
            ))}
          </select>
        </div>

        {formData.deliveryMethod === 'email' && (
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500, color: '#374151' }}>
              Email Recipients
            </label>
            <input
              type="text"
              value={formData.recipients}
              onChange={(e) => setFormData({ ...formData, recipients: e.target.value })}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '14px',
              }}
              placeholder="email1@example.com, email2@example.com"
            />
          </div>
        )}

        {formData.deliveryMethod === 'webhook' && (
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500, color: '#374151' }}>Webhook URL</label>
            <input
              type="url"
              value={formData.webhookUrl}
              onChange={(e) => setFormData({ ...formData, webhookUrl: e.target.value })}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '14px',
              }}
              placeholder="https://api.example.com/webhook"
            />
          </div>
        )}

        <div style={{ gridColumn: '1 / -1' }}>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500, color: '#374151' }}>
            Filters (JSON)
          </label>
          <textarea
            value={formData.filters}
            onChange={(e) => setFormData({ ...formData, filters: e.target.value })}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'monospace',
              minHeight: '80px',
            }}
            placeholder='{"markets": ["DE", "FR"], "dateRange": "last_30_days"}'
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
        <button
          onClick={handleCreateSchedule}
          disabled={!formData.name || !formData.reportType || !formData.frequency}
          style={{
            padding: '10px 20px',
            backgroundColor: formData.name && formData.reportType && formData.frequency ? '#4A90E2' : '#9CA3AF',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: formData.name && formData.reportType && formData.frequency ? 'pointer' : 'not-allowed',
            fontWeight: 500,
          }}
        >
          Create Schedule
        </button>
        <button
          onClick={() => setActiveTab('schedules')}
          style={{
            padding: '10px 20px',
            backgroundColor: 'white',
            color: '#374151',
            border: '1px solid #D1D5DB',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: 500,
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  )

  const renderStatistics = () => (
    <div>
      <h3 style={{ margin: '0 0 24px 0', color: '#1F2937' }}>Statistics</h3>

      {!statistics ? (
        <p>Loading statistics...</p>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
            <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#1F2937' }}>{statistics.totalSchedules}</div>
              <div style={{ color: '#6B7280', fontSize: '14px' }}>Total Schedules</div>
            </div>
            <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#10B981' }}>{statistics.activeSchedules}</div>
              <div style={{ color: '#6B7280', fontSize: '14px' }}>Active Schedules</div>
            </div>
            <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#1F2937' }}>{statistics.totalExecutions}</div>
              <div style={{ color: '#6B7280', fontSize: '14px' }}>Total Executions</div>
            </div>
            <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#4A90E2' }}>{statistics.successRate}%</div>
              <div style={{ color: '#6B7280', fontSize: '14px' }}>Success Rate</div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
              <h4 style={{ margin: '0 0 16px 0', color: '#1F2937' }}>By Report Type</h4>
              {Object.entries(statistics.byType).map(([type, count]) => (
                <div
                  key={type}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '8px 0',
                    borderBottom: '1px solid #F3F4F6',
                  }}
                >
                  <span style={{ color: '#4B5563' }}>{type}</span>
                  <span style={{ fontWeight: 600, color: '#1F2937' }}>{count}</span>
                </div>
              ))}
            </div>

            <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
              <h4 style={{ margin: '0 0 16px 0', color: '#1F2937' }}>By Frequency</h4>
              {Object.entries(statistics.byFrequency).map(([freq, count]) => (
                <div
                  key={freq}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '8px 0',
                    borderBottom: '1px solid #F3F4F6',
                  }}
                >
                  <span style={{ color: '#4B5563' }}>{freq}</span>
                  <span style={{ fontWeight: 600, color: '#1F2937' }}>{count}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F9FAFB' }}>
      <Header />

      <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ margin: '0 0 8px 0', fontSize: '24px', color: '#1F2937' }}>Scheduled Reports</h1>
          <p style={{ margin: 0, color: '#6B7280' }}>Automate report generation and delivery</p>
        </div>

        {error && (
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: '#FEE2E2',
              color: '#991B1B',
              borderRadius: '8px',
              marginBottom: '16px',
            }}
          >
            {error}
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '1px solid #E5E7EB', paddingBottom: '12px' }}>
          {[
            { id: 'schedules' as const, label: 'Schedules' },
            { id: 'executions' as const, label: 'History' },
            { id: 'create' as const, label: 'Create New' },
            { id: 'statistics' as const, label: 'Statistics' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '8px 16px',
                backgroundColor: activeTab === tab.id ? '#4A90E2' : 'transparent',
                color: activeTab === tab.id ? 'white' : '#6B7280',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 500,
                fontSize: '14px',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', border: '1px solid #E5E7EB' }}>
          {activeTab === 'schedules' && renderSchedules()}
          {activeTab === 'executions' && renderExecutions()}
          {activeTab === 'create' && renderCreateForm()}
          {activeTab === 'statistics' && renderStatistics()}
        </div>

        {/* Schedule Details Modal */}
        {selectedSchedule && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
            }}
            onClick={() => setSelectedSchedule(null)}
          >
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '24px',
                maxWidth: '600px',
                width: '90%',
                maxHeight: '80vh',
                overflow: 'auto',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, color: '#1F2937' }}>{selectedSchedule.name}</h3>
                <button
                  onClick={() => setSelectedSchedule(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#6B7280',
                  }}
                >
                  &times;
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', fontSize: '14px' }}>
                <div>
                  <strong>Status:</strong> {getStatusBadge(selectedSchedule.status)}
                </div>
                <div>
                  <strong>Report Type:</strong> {selectedSchedule.reportType}
                </div>
                <div>
                  <strong>Frequency:</strong> {selectedSchedule.frequency}
                </div>
                <div>
                  <strong>Time:</strong> {selectedSchedule.timeOfDay} {selectedSchedule.timezone}
                </div>
                <div>
                  <strong>Output Format:</strong> {selectedSchedule.outputFormat.toUpperCase()}
                </div>
                <div>
                  <strong>Delivery:</strong> {selectedSchedule.deliveryMethod}
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <strong>Description:</strong>
                  <p style={{ margin: '4px 0 0 0', color: '#4B5563' }}>
                    {selectedSchedule.description || 'No description'}
                  </p>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <strong>Filters:</strong>
                  <pre
                    style={{
                      margin: '4px 0 0 0',
                      padding: '8px',
                      backgroundColor: '#F3F4F6',
                      borderRadius: '4px',
                      fontSize: '12px',
                      overflow: 'auto',
                    }}
                  >
                    {JSON.stringify(selectedSchedule.filters, null, 2)}
                  </pre>
                </div>
                <div>
                  <strong>Last Run:</strong> {formatDateTime(selectedSchedule.lastRun)}
                </div>
                <div>
                  <strong>Next Run:</strong> {formatDateTime(selectedSchedule.nextRun)}
                </div>
                <div>
                  <strong>Total Runs:</strong> {selectedSchedule.runCount}
                </div>
                <div>
                  <strong>Created:</strong> {formatDateTime(selectedSchedule.createdAt)}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
